import type { UiText } from '@ory/client';
import { getWebhookMessageContext } from '@/lib/auth/ory/service/types';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { getShortCode } from '@/lib/i18n/config/languages';
import { formatDateTimeToLocal } from '@/utils/date/formatDateTimeToLocal';
import { parseISODateTime } from '@/utils/date/parseISODateTime';

/**
 * context.displayMessages からユーザーのロケールに合うメッセージを取得
 *
 * Kratos の flow-interrupting webhook が返す displayMessages は
 * { en: "...", ja: "...", id: "...", vn: "...", th: "...", zhCN: "..." }
 * の形式で、キーは LANGUAGE_CONFIG の shortCode に対応する。
 */
function resolveDisplayMessage(context: object, language: string): string | undefined {
  const webhookCtx = getWebhookMessageContext(context);
  const displayMessages = webhookCtx?.displayMessages;
  if (!displayMessages) {
    return undefined;
  }
  const shortCode = getShortCode(language);
  return displayMessages[shortCode] ?? displayMessages.en;
}

/**
 * i18n の言語コードを Intl 用の BCP-47 ロケールに補正する。
 * vn-VN は BCP-47 上 Vietnamese (vi) ではないため変換する。
 */
function toIntlLocale(language?: string): string | undefined {
  return language === 'vn-VN' ? 'vi-VN' : language;
}

/**
 * UiTextを翻訳する
 *
 * 翻訳の優先順位:
 * 1. context.displayMessages[ユーザーのロケール] (webhook からの多言語メッセージ)
 * 2. i18n キー (pages.auth.ory.{id}) によるフロントエンド翻訳
 * 3. uiText.text (Kratos のデフォルトテキスト)
 */
export function translateUiText(uiText: UiText, t: (key: string) => string, language?: string): string {
  // Webhook から多言語メッセージが提供されている場合はそれを優先
  if (language && uiText.context) {
    const displayMessage = resolveDisplayMessage(uiText.context, language);
    if (displayMessage) {
      return displayMessage;
    }
  }
  let text = uiText.text;

  // IDから翻訳を取得
  if (uiText.id) {
    const translationKey = String(uiText.id);
    const translated = t(translationKey);
    if (translated && typeof translated === 'string' && translated !== translationKey) {
      text = translated;
    }
  }

  // コンテキスト変数を置換。expired_at 等の UTC ISO 日時はローカル表記に整形する
  if (uiText.context && typeof uiText.context === 'object') {
    const intlLocale = toIntlLocale(language);
    for (const [key, value] of Object.entries(uiText.context)) {
      if (value != null && typeof value !== 'object') {
        const raw = String(value);
        const date = parseISODateTime(raw);
        const stringValue = date ? formatDateTimeToLocal(date, intlLocale) : raw;
        text = text.replace(`{${key}}`, stringValue);
      }
    }
  }
  return text;
}

/**
 * Ory UI Node用の翻訳フック
 *
 * UiTextオブジェクト（messagesやmeta.label）を翻訳するためのフック
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#ui-message-codes
 */
export function useNodeTranslation() {
  const {
    t,
    i18n
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth.ory'
  });

  /**
   * UiTextを翻訳する
   *
   * messagesやmeta.labelのどちらにも使用できます
   */
  const translateUiTextWrapper = (uiText: UiText): string => {
    // Kratos の UiText.id は動的な数値IDのため、TFunction のリテラル型キーと互換性がない。
    // 翻訳キーが見つからなければ translateUiText 内でフォールバックテキストを使用する。
    const tFn = (key: string): string => String(t(key as never));
    return translateUiText(uiText, tFn, i18n.language);
  };
  return {
    translateUiText: translateUiTextWrapper
  };
}